import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  TablePagination
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  ArticleOutlined as ArticleOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { getCustomPages } from '../../../services/ecommerceApi';

interface PagePickerDialogProps {
  open: boolean;
  configId: string;
  onClose: () => void;
  onSelect: (selection: { url: string; title: string; slug: string }) => void;
}

interface PageRow {
  id: string;
  title: string;
  slug: string;
  url: string;
  isActive: boolean;
  displayOrder: number;
  updatedAt?: string;
  builtIn?: boolean;
}

// Tüm hesaplar için sabit olarak sunulan, e-ticaret sitesinin yerleşik sayfaları.
// Bu liste; Hakkımızda, Hizmetler, İletişim gibi default route'lara karşılık gelir.
const BUILT_IN_PAGES: PageRow[] = [
  { id: 'builtin_home', title: 'Anasayfa', slug: 'home', url: '/', isActive: true, displayOrder: -100, builtIn: true },
  { id: 'builtin_about', title: 'Hakkımızda', slug: 'about', url: '/about', isActive: true, displayOrder: -80, builtIn: true },
  { id: 'builtin_services', title: 'Hizmetler', slug: 'services', url: '/services', isActive: true, displayOrder: -70, builtIn: true },
  { id: 'builtin_products_solutions', title: 'Ürün & Çözümler', slug: 'products-solutions', url: '/products-solutions', isActive: true, displayOrder: -60, builtIn: true },
  { id: 'builtin_references', title: 'Referanslar', slug: 'references', url: '/references', isActive: true, displayOrder: -50, builtIn: true },
  { id: 'builtin_branches', title: 'Şubeler', slug: 'branches', url: '/branches', isActive: true, displayOrder: -45, builtIn: true },
  { id: 'builtin_contact', title: 'İletişim', slug: 'contact', url: '/contact', isActive: true, displayOrder: -40, builtIn: true },
  { id: 'builtin_faq', title: 'SSS', slug: 'faq', url: '/account/faq', isActive: true, displayOrder: -30, builtIn: true },
  { id: 'builtin_become_seller', title: 'Franchise / Satıcı Ol', slug: 'become-seller', url: '/become-seller', isActive: true, displayOrder: -20, builtIn: true },
  { id: 'builtin_cart', title: 'Sepet', slug: 'cart', url: '/cart', isActive: true, displayOrder: -10, builtIn: true },
];

const PagePickerDialog: React.FC<PagePickerDialogProps> = ({ open, configId, onClose, onSelect }) => {
  const [pages, setPages] = useState<PageRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    if (!open || !configId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setQuery('');
    setPage(0);
    getCustomPages(configId)
      .then((res) => {
        if (cancelled) return;
        const customList: PageRow[] = (res?.data ?? []).map((p: any) => ({
          id: String(p?.id ?? ''),
          title: String(p?.title ?? ''),
          slug: String(p?.slug ?? ''),
          url: `/pages/${String(p?.slug ?? '')}`,
          isActive: p?.isActive !== false,
          displayOrder: Number(p?.displayOrder ?? 0),
          updatedAt: p?.updatedAt,
          builtIn: false
        }));
        customList.sort((a, b) => a.displayOrder - b.displayOrder);
        // Yerleşik (default) sayfalar her zaman üstte; özel sayfalar onların altında.
        setPages([...BUILT_IN_PAGES, ...customList]);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err?.response?.data?.message || err?.message || 'Sayfalar getirilemedi';
        setError(message);
        // Backend'e ulaşılamasa bile en azından yerleşik sayfalar seçilebilir kalsın.
        setPages([...BUILT_IN_PAGES]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, configId]);

  const filtered = useMemo(() => {
    if (!query.trim()) return pages;
    const q = query.trim().toLowerCase();
    return pages.filter((p) =>
      p.title.toLowerCase().includes(q)
      || p.slug.toLowerCase().includes(q)
      || p.url.toLowerCase().includes(q)
    );
  }, [pages, query]);

  const pagedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [query, open]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, filtered.length, rowsPerPage]);

  const handlePick = (page: PageRow) => {
    onSelect({ url: page.url, title: page.title, slug: page.slug });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <ArticleOutlinedIcon color="primary" fontSize="small" />
        <Typography variant="h6" component="span" sx={{ flex: 1 }}>
          Sayfa Seç
        </Typography>
        <Tooltip title="Kapat">
          <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Sayfa ara (başlık veya slug)..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <ArticleOutlinedIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {pages.length === 0 ? 'Henüz oluşturulmuş bir sayfa yok' : 'Eşleşen sayfa bulunamadı'}
            </Typography>
            <Typography variant="caption">
              {pages.length === 0 && 'İÇERİK → Sayfalar sekmesinden yeni sayfa oluşturabilirsiniz.'}
            </Typography>
          </Box>
        ) : (
          <List
            sx={{
              maxHeight: 380,
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 0
            }}
          >
            {pagedItems.map((p) => (
              <ListItemButton
                key={p.id}
                onClick={() => handlePick(p)}
                sx={{
                  alignItems: 'flex-start',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 0 },
                  py: 1.25
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>{p.title || 'Adsız Sayfa'}</Typography>
                      {p.builtIn && (
                        <Chip
                          size="small"
                          label="Hazır Sayfa"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {!p.isActive && (
                        <Chip
                          size="small"
                          icon={<VisibilityOffIcon sx={{ fontSize: 12 }} />}
                          label="Pasif"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <OpenInNewIcon sx={{ fontSize: 13, opacity: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        {p.url}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
        {!loading && filtered.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Sayfa başına"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PagePickerDialog;
